import { Shield, ShieldOff, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sender } from '@/types/sender'

interface SenderCardProps {
  sender: Sender
  onToggleBlock: (senderId: number, currentStatus: boolean) => void
  isFirst?: boolean
  isLast?: boolean
  timezone?: string
}

export default function SenderCard({
  sender,
  onToggleBlock,
  isFirst = false,
  isLast = false,
  timezone = 'America/New_York',
}: SenderCardProps) {
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never'

    const date = new Date(dateString)
    const now = new Date()
    const currentYear = now.getFullYear()
    const dateYear = date.getFullYear()

    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      month: 'short',
      day: 'numeric',
      year: dateYear < currentYear ? 'numeric' : undefined,
    })

    return formatter.format(date)
  }

  const roundedCorners = isFirst && isLast
    ? 'rounded-lg'
    : isFirst
      ? 'rounded-t-lg'
      : isLast
        ? 'rounded-b-lg'
        : ''

  return (
    <div
      className={`transition-colors bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 ${roundedCorners} ${!isLast ? 'border-b border-gray-300 dark:border-gray-700/50' : ''}`}
    >
      <div className="px-6 py-3 sm:py-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-3">
              <Mail className="w-4 h-4 text-gray-400 dark:text-gray-500 mt-1 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                {/* Email and Name */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
                  <p className="font-semibold text-sm sm:text-base text-blue-950 dark:text-white truncate">
                    {sender.emailAddress}
                  </p>
                  {sender.senderName && (
                    <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                      {sender.senderName}
                    </span>
                  )}
                  {sender.blocked && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                      Blocked
                    </span>
                  )}
                </div>

                {/* Stats */}
                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                  <span>
                    <span className="font-medium text-gray-700 dark:text-gray-300">{sender.emailCount}</span> {sender.emailCount === 1 ? 'email' : 'emails'}
                  </span>
                  <span className="text-gray-300 dark:text-gray-600">•</span>
                  <span>First seen {formatDate(sender.firstSeenAt)}</span>
                  <span className="text-gray-300 dark:text-gray-600">•</span>
                  <span>Last seen {formatDate(sender.lastSeenAt)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Block/Unblock Button */}
          <div className="flex items-center flex-shrink-0">
            <Button
              variant={sender.blocked ? 'default' : 'secondary'}
              size="sm"
              onClick={() => onToggleBlock(sender.id, sender.blocked)}
              className={`h-auto px-3 py-1.5 text-xs ${
                sender.blocked
                  ? 'bg-green-600 hover:bg-green-700 text-white dark:bg-green-600 dark:hover:bg-green-700'
                  : 'dark:bg-gray-700 dark:hover:bg-gray-600'
              }`}
              type="button"
            >
              {sender.blocked ? (
                <>
                  <ShieldOff className="w-3 h-3 mr-1.5" />
                  Unblock
                </>
              ) : (
                <>
                  <Shield className="w-3 h-3 mr-1.5" />
                  Block
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
